import React from "react";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";
import { fetchInfModels } from "../influencers/infModelsApi";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Button,
  alpha,
  useTheme,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  Alert,
  MenuItem,
} from "@mui/material";
import {
  CampaignOutlined,
  ManageSearchOutlined,
  ArticleOutlined,
  MovieCreationOutlined,
  RocketLaunchOutlined,
  ArrowForwardOutlined,
  ArrowBackOutlined,
  Face2,
  SlowMotionVideo,
  ContentCopyOutlined,
  ExpandMore,
  DeleteOutline,
  RefreshOutlined,
  LinkOutlined,
  ImageOutlined,
  VolumeUpOutlined,
} from "@mui/icons-material";
import ModelCard from "../scripVideo/components/ModelCard";
import { mediaSrc } from "../../utils/mediaSrc";
import axios from "axios";

/** Sync sidebar + URL when navigating within /user (matches UserDashboard popstate handler). */
function goToUserPage(pageId) {
  const params = new URLSearchParams(window.location.search);
  params.set("page", pageId);
  window.history.pushState(
    {},
    "",
    `${window.location.pathname}?${params.toString()}`,
  );
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const STEP_KEYS = [
  "character",
  "research",
  "reviewResearch",
  "scriptPrompt",
  "scripts",
  "scale",
];

const AVATAR_REGION_FILTERS = [
  { id: "All", label: "All regions" },
  { id: "Americas", label: "Americas" },
  { id: "Asia", label: "Asia-Pacific" },
  { id: "Europe", label: "Europe" },
  { id: "UK", label: "UK & Ireland" },
  { id: "Africa & Middle East", label: "Africa & Middle East" },
  { id: "Global", label: "Global / neutral" },
];

function base64ToMp3Blob(b64) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: "audio/mpeg" });
}

const UgcAdsWorkflow = ({ lang }) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  /** 'url' | 'image' — only one input shown at a time */
  const [productInputMode, setProductInputMode] = React.useState("url");
  const [productUrl, setProductUrl] = React.useState("");
  const [researchPrompt, setResearchPrompt] = React.useState("");
  const [productFile, setProductFile] = React.useState(null);
  const [productPreviewUrl, setProductPreviewUrl] = React.useState(null);

  const [activeStep, setActiveStep] = React.useState(0);

  const [inf, setInf] = React.useState([]);
  const [selectedModel, setSelectedModel] = React.useState(null);
  const [savingCharacter, setSavingCharacter] = React.useState(false);

  const [workflowId, setWorkflowId] = React.useState(null);
  const [researchResult, setResearchResult] = React.useState("");
  const [scriptPrompt, setScriptPrompt] = React.useState("");
  const [scripts, setScripts] = React.useState([]);

  const [loadingResearch, setLoadingResearch] = React.useState(false);
  const [loadingScripts, setLoadingScripts] = React.useState(false);
  const [history, setHistory] = React.useState([]);

  const [avatarJobId, setAvatarJobId] = React.useState(null);
  const [avatarVideo, setAvatarVideo] = React.useState(null);
  const [avatarErrorMessage, setAvatarErrorMessage] = React.useState(null);
  const [avatarScriptPick, setAvatarScriptPick] = React.useState(0);
  const [loadingAvatar, setLoadingAvatar] = React.useState(false);

  const [avatarTtsMode, setAvatarTtsMode] = React.useState("kie");
  const [avatarKieVoices, setAvatarKieVoices] = React.useState([]);
  const [avatarOpenaiVoices, setAvatarOpenaiVoices] = React.useState([]);
  const [avatarGenderFilter, setAvatarGenderFilter] = React.useState("all");
  const [avatarRegionFilter, setAvatarRegionFilter] = React.useState("All");
  const [avatarVoiceId, setAvatarVoiceId] = React.useState("");
  const [avatarPreviewText, setAvatarPreviewText] = React.useState("");
  const [avatarPreviewUrl, setAvatarPreviewUrl] = React.useState(null);
  const [avatarPreviewLoading, setAvatarPreviewLoading] = React.useState(false);

  const applyAvatarFromRow = React.useCallback((row) => {
    if (!row || typeof row !== "object") return;
    setAvatarJobId(row.avatar_job_id ?? null);
    setAvatarVideo(row.avatar_video ?? null);
    setAvatarErrorMessage(row.avatar_error_message ?? null);
    if (row.avatar_script_index != null && Number.isFinite(Number(row.avatar_script_index))) {
      setAvatarScriptPick(Number(row.avatar_script_index));
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (productPreviewUrl) URL.revokeObjectURL(productPreviewUrl);
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    };
  }, [productPreviewUrl, avatarPreviewUrl]);

  const stepLabels = React.useMemo(
    () => [
      lang?.wizardStepCharacter || "Character",
      lang?.wizardStepResearch || "Research",
      lang?.wizardStepReview || "Review",
      lang?.wizardStepScriptsPrompt || "Script prompt",
      lang?.wizardStepScriptsOut || "Scripts",
      lang?.wizardStepScale || "Scale",
    ],
    [lang],
  );

  const activeCharacters = React.useMemo(
    () => (inf || []).filter((m) => String(m?.status || "").toLowerCase() === "active"),
    [inf],
  );

  async function fetchInf() {
    const res = await fetchInfModels(hitAxios);
    if (res?.data?.success && Array.isArray(res.data.data)) {
      setInf(res.data.data);
    }
  }

  React.useEffect(() => {
    fetchInf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = React.useCallback(async () => {
    const res = await hitAxios({
      path: "/api/ugc_workflow/get_all",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success && Array.isArray(res.data.data)) {
      setHistory(res.data.data);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    if (!workflowId || !avatarJobId) return undefined;
    let cancelled = false;
    const tick = async () => {
      const res = await hitAxios({
        path: `/api/ugc_workflow/get_one/${workflowId}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (cancelled || !res?.data?.success || !res.data.data) return;
      applyAvatarFromRow(res.data.data);
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [workflowId, avatarJobId, hitAxios, applyAvatarFromRow]);

  React.useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const filteredKieVoices = React.useMemo(() => {
    if (!Array.isArray(avatarKieVoices)) return [];
    return avatarKieVoices.filter((v) => {
      if (avatarGenderFilter !== "all" && v.gender !== avatarGenderFilter) return false;
      if (avatarRegionFilter === "All") return true;
      return Array.isArray(v.regions) && v.regions.includes(avatarRegionFilter);
    });
  }, [avatarKieVoices, avatarGenderFilter, avatarRegionFilter]);

  React.useEffect(() => {
    if (avatarTtsMode !== "kie") return;
    if (!filteredKieVoices.length) return;
    if (!filteredKieVoices.some((v) => v.id === avatarVoiceId)) {
      setAvatarVoiceId(filteredKieVoices[0].id);
    }
  }, [avatarTtsMode, filteredKieVoices, avatarVoiceId]);

  React.useEffect(() => {
    if (avatarTtsMode !== "openai" || !avatarOpenaiVoices.length) return;
    if (!avatarOpenaiVoices.some((v) => v.id === avatarVoiceId)) {
      setAvatarVoiceId(avatarOpenaiVoices[0].id);
    }
  }, [avatarTtsMode, avatarOpenaiVoices, avatarVoiceId]);

  React.useEffect(() => {
    if (activeStep !== 4 || scripts.length !== 10) return undefined;
    let cancelled = false;
    (async () => {
      const res = await hitAxios({
        path: "/api/ugc_workflow/avatar_voice_options",
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (cancelled || !res?.data?.success || !res.data.data) return;
      const d = res.data.data;
      setAvatarTtsMode(d.tts_mode === "openai" ? "openai" : "kie");
      setAvatarKieVoices(Array.isArray(d.kie_voices) ? d.kie_voices : []);
      setAvatarOpenaiVoices(Array.isArray(d.openai_voices) ? d.openai_voices : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeStep, scripts.length, hitAxios]);

  const handleProductModeChange = (_e, value) => {
    if (value === null) return;
    setProductInputMode(value);
    if (value === "url") {
      setProductFile(null);
      if (productPreviewUrl) URL.revokeObjectURL(productPreviewUrl);
      setProductPreviewUrl(null);
    } else {
      setProductUrl("");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setProductFile(file);
    if (productPreviewUrl) URL.revokeObjectURL(productPreviewUrl);
    setProductPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const runResearch = async () => {
    const rp = researchPrompt.trim();
    const pu = productUrl.trim();

    if (!rp) {
      showSnack(lang?.ugcResearchPromptRequired || "Enter a research prompt.", false);
      return false;
    }

    if (productInputMode === "url") {
      if (!pu) {
        showSnack(lang?.ugcEnterProductUrl || "Enter a product URL.", false);
        return false;
      }
    } else if (!productFile) {
      showSnack(lang?.ugcUploadProductImage || "Upload a product image.", false);
      return false;
    }

    if (!selectedModel?.id) {
      showSnack(lang?.ugcSelectCharacterFirst || "Select a character first.", false);
      return false;
    }

    const fd = new FormData();
    fd.append("research_prompt", rp);
    fd.append("model_id", String(selectedModel.id));
    if (productInputMode === "url") fd.append("product_url", pu);
    if (productInputMode === "image" && productFile) fd.append("product_image", productFile);

    setLoadingResearch(true);
    const res = await hitAxios({
      path: "/api/ugc_workflow/research",
      post: true,
      obj: fd,
      showSnackbar: true,
      showLoading: false,
    });
    setLoadingResearch(false);

    if (res?.data?.success && res.data.data?.id) {
      const row = res.data.data;
      setWorkflowId(row.id);
      setResearchResult(row.research_result || "");
      setScripts(Array.isArray(row.scripts) ? row.scripts : []);
      setScriptPrompt(row.script_prompt || "");
      if (row.model) setSelectedModel(row.model);
      applyAvatarFromRow(row);
      await fetchHistory();
      return true;
    }
    return false;
  };

  const runScripts = async () => {
    if (!workflowId) {
      showSnack(lang?.ugcCompleteResearchFirst || "Complete research first.", false);
      return false;
    }
    const sp = scriptPrompt.trim();
    if (!sp) {
      showSnack(lang?.ugcScriptPromptRequired || "Enter a scriptwriting prompt.", false);
      return false;
    }

    setLoadingScripts(true);
    const res = await hitAxios({
      path: "/api/ugc_workflow/generate_scripts",
      post: true,
      obj: { id: workflowId, script_prompt: sp },
      showSnackbar: true,
      showLoading: false,
    });
    setLoadingScripts(false);

    if (res?.data?.success && res.data.data?.scripts) {
      const row = res.data.data;
      setScripts(row.scripts || []);
      if (row.model) setSelectedModel(row.model);
      applyAvatarFromRow(row);
      await fetchHistory();
      return true;
    }
    return false;
  };

  const playAvatarStyleSample = React.useCallback(() => {
    const v =
      filteredKieVoices.find((x) => x.id === avatarVoiceId) ||
      avatarKieVoices.find((x) => x.id === avatarVoiceId);
    if (!v?.sampleUrl) {
      showSnack("No built-in sample for this voice.", false);
      return;
    }
    const a = new Audio(v.sampleUrl);
    a.play().catch(() => showSnack("Could not play sample (network or browser).", false));
  }, [filteredKieVoices, avatarKieVoices, avatarVoiceId, showSnack]);

  const requestAvatarPreviewTts = React.useCallback(
    async (mode) => {
      const tokenKey = `${process.env.REACT_APP_TOKEN || "ahead_ai"}_user`;
      const token = localStorage.getItem(tokenKey);
      const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
      let text = "";
      if (mode === "script") {
        text = String(scripts[avatarScriptPick] || "").trim().slice(0, 260);
        if (!text) {
          showSnack("Selected script is empty.", false);
          return;
        }
      } else {
        text = avatarPreviewText.trim().slice(0, 280);
        if (!text) {
          showSnack("Enter a custom preview line, or use “first lines of script”.", false);
          return;
        }
      }
      const body =
        avatarTtsMode === "openai"
          ? { text, openai_speech_voice: avatarVoiceId }
          : { text, kie_tts_voice: avatarVoiceId };
      setAvatarPreviewLoading(true);
      try {
        const res = await axios.post(`${base}/api/ugc_workflow/preview_tts`, body, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
          timeout: 120000,
        });
        if (!res.data?.success || !res.data.data?.audio_base64) {
          showSnack(res.data?.msg || "Preview failed", false);
          return;
        }
        setAvatarPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          const blob = base64ToMp3Blob(res.data.data.audio_base64);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        const msg = e?.response?.data?.msg || e?.message || "Preview failed";
        showSnack(msg, false);
      } finally {
        setAvatarPreviewLoading(false);
      }
    },
    [scripts, avatarScriptPick, avatarPreviewText, avatarTtsMode, avatarVoiceId, showSnack],
  );

  const startAvatarVideo = async () => {
    if (!workflowId) {
      showSnack(lang?.ugcCompleteResearchFirst || "Complete research first.", false);
      return;
    }
    if (scripts?.length !== 10) {
      showSnack(lang?.scriptsPendingHint || "Generate all ten scripts first.", false);
      return;
    }
    if (avatarJobId) {
      showSnack(lang?.ugcAvatarWaitPoll || "A video is already generating; wait for it to finish.", false);
      return;
    }
    if (!avatarVoiceId) {
      showSnack("Select a voice template first.", false);
      return;
    }

    setLoadingAvatar(true);
    const res = await hitAxios({
      path: "/api/ugc_workflow/start_avatar_video",
      post: true,
      obj: {
        id: workflowId,
        script_index: avatarScriptPick,
        ...(avatarTtsMode === "openai"
          ? { openai_speech_voice: avatarVoiceId }
          : { kie_tts_voice: avatarVoiceId }),
      },
      showSnackbar: true,
      showLoading: false,
    });
    setLoadingAvatar(false);

    if (res?.data?.success && res.data.data) {
      applyAvatarFromRow(res.data.data);
      await fetchHistory();
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!selectedModel?.id) {
        showSnack(lang?.ugcSelectCharacterFirst || "Select a character to continue.", false);
        return;
      }
      setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      const ok = await runResearch();
      if (ok) setActiveStep(2);
      return;
    }
    if (activeStep === 2) {
      setActiveStep(3);
      return;
    }
    if (activeStep === 3) {
      const ok = await runScripts();
      if (ok) setActiveStep(4);
      return;
    }
    if (activeStep === 4) {
      setActiveStep(5);
      return;
    }
  };

  const saveWorkflowCharacter = async (model) => {
    if (!workflowId || !model?.id) return;
    if (selectedModel?.id === model.id) return;
    setSavingCharacter(true);
    const res = await hitAxios({
      path: "/api/ugc_workflow/set_character",
      post: true,
      obj: { id: workflowId, model_id: model.id },
      showLoading: false,
      showSnackbar: true,
    });
    setSavingCharacter(false);
    if (res?.data?.success && res.data.data?.model) {
      setSelectedModel(res.data.data.model);
      applyAvatarFromRow(res.data.data);
      await fetchHistory();
    }
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnack(`${label || "Copied"}`, true);
    } catch {
      showSnack("Could not copy", false);
    }
  };

  const inferStepFromRow = (row) => {
    const hasScripts = Array.isArray(row.scripts) && row.scripts.length === 10;
    const hasResearch = Boolean(String(row.research_result || "").trim());
    if (hasScripts) return 4;
    if (hasResearch) return 2;
    return 0;
  };

  const loadWorkflow = (row) => {
    setWorkflowId(row.id);
    setProductUrl(row.product_url || "");
    setResearchPrompt(row.research_prompt || "");
    setResearchResult(row.research_result || "");
    setScriptPrompt(row.script_prompt || "");
    setScripts(Array.isArray(row.scripts) ? row.scripts : []);
    setProductFile(null);
    if (productPreviewUrl) URL.revokeObjectURL(productPreviewUrl);
    setProductPreviewUrl(null);

    if (row.product_image && !row.product_url) {
      setProductInputMode("image");
    } else {
      setProductInputMode("url");
    }

    if (row.model && typeof row.model === "object") {
      setSelectedModel(row.model);
    } else {
      setSelectedModel(null);
    }

    applyAvatarFromRow(row);

    setActiveStep(inferStepFromRow(row));
    showSnack(lang?.ugcWorkflowLoaded || "Workflow loaded", true);
  };

  const deleteWorkflow = async (id) => {
    const res = await hitAxios({
      path: "/api/ugc_workflow/del_one",
      post: true,
      obj: { id },
      showLoading: false,
    });
    if (res?.data?.success) {
      if (workflowId === id) {
        setWorkflowId(null);
        setResearchResult("");
        setScripts([]);
        setSelectedModel(null);
        setActiveStep(0);
        setAvatarJobId(null);
        setAvatarVideo(null);
        setAvatarErrorMessage(null);
        setAvatarScriptPick(0);
        setAvatarTtsMode("kie");
        setAvatarKieVoices([]);
        setAvatarOpenaiVoices([]);
        setAvatarGenderFilter("all");
        setAvatarRegionFilter("All");
        setAvatarVoiceId("");
        setAvatarPreviewText("");
        setAvatarPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setAvatarPreviewLoading(false);
      }
      await fetchHistory();
    }
  };

  const title = lang?.ugcAdsWorkflowTitle || "Ads Workflow";
  const subtitle =
    lang?.ugcAdsWorkflowSub || "Character → research → scripts → scale (switch character anytime after scripts).";

  const showNext = activeStep >= 0 && activeStep <= 4;
  const nextDisabled =
    (activeStep === 0 && (!selectedModel?.id || savingCharacter)) ||
    (activeStep === 1 && loadingResearch) ||
    (activeStep === 3 && (loadingScripts || !workflowId || !researchResult));

  const nextLabel = (() => {
    if (activeStep === 0) return lang?.nextToResearch || "Next: product & research";
    if (activeStep === 1) return lang?.nextRunResearch || "Next: run research";
    if (activeStep === 2) return lang?.nextContinue || "Next";
    if (activeStep === 3) return lang?.nextGenerateScripts || "Next: generate 10 scripts";
    if (activeStep === 4) return lang?.nextScale || "Next: scale & finish";
    return lang?.next || "Next";
  })();

  return (
    <div>
      <PageHeader icon={CampaignOutlined} title={title} subtitle={subtitle} />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ overflowX: "auto", pb: 0.5 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isNarrow}
          sx={{
            mb: 2,
            ...(isNarrow && {
              minWidth: 680,
              px: 1,
            }),
            "& .MuiStepLabel-label": { typography: "caption", fontWeight: 600 },
          }}
        >
          {stepLabels.map((label, i) => (
            <Step key={STEP_KEYS[i]}>
              <StepLabel>
                {isNarrow ? (
                  <Box component="span" sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                    {label}
                  </Box>
                ) : (
                  label
                )}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        </Box>

        {isNarrow ? (
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 1 }}>
            {stepLabels[activeStep]}
          </Typography>
        ) : null}

        <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0, sm: 1 } }}>
          {lang?.ugcHeroLead ||
            "Pick your character first, then product and research. After scripts you can switch character for export."}
        </Typography>
      </Paper>

      {/* ── Step content ───────────────────────────────── */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          minHeight: 320,
        }}
      >
        {/* Step 0 — Character */}
        {activeStep === 0 && (
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Face2 color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {lang?.ugcStep0CharacterHeading || "Step 1 — Choose your character"}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {lang?.ugcStep0CharacterBody ||
                "Select the on-camera persona for this UGC batch. Research and scripts will be tailored to this character. You can change it again after scripts are generated."}
            </Typography>
            {activeCharacters.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {lang?.ugcNoActiveCharacters || "No active characters yet. Create one under Characters."}
                </Typography>
                <Button variant="outlined" size="small" onClick={() => goToUserPage("influencers")}>
                  {lang?.manageCharacters || "Characters"}
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {activeCharacters.map((m) => (
                  <Grid item xs={12} sm={6} md={4} key={m.id}>
                    <ModelCard
                      model={m}
                      isSelected={selectedModel?.id === m.id}
                      onSelect={() => setSelectedModel(m)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Stack>
        )}

        {/* Step 1 — Research input */}
        {activeStep === 1 && (
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={1}>
              <ManageSearchOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {lang?.ugcStep1Heading || "Step 2 — Research"}
              </Typography>
              {workflowId ? (
                <Chip size="small" label={`ID ${workflowId}`} color="primary" variant="outlined" />
              ) : null}
              {selectedModel?.name ? (
                <Chip size="small" icon={<Face2 sx={{ "&&": { fontSize: 16 } }} />} label={selectedModel.name} />
              ) : null}
            </Stack>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {lang?.productContextMode || "Product context"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {lang?.productContextHint || "Choose one: paste a link or upload an image — not both."}
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={productInputMode}
                onChange={handleProductModeChange}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="url">
                  <LinkOutlined sx={{ mr: 1, fontSize: 18 }} />
                  {lang?.productUrl || "Product URL"}
                </ToggleButton>
                <ToggleButton value="image">
                  <ImageOutlined sx={{ mr: 1, fontSize: 18 }} />
                  {lang?.uploadProductImage || "Upload image"}
                </ToggleButton>
              </ToggleButtonGroup>

              {productInputMode === "url" ? (
                <TextField
                  fullWidth
                  size="small"
                  label={lang?.productUrl || "Product URL"}
                  placeholder="https://..."
                  value={productUrl}
                  onChange={(e) => setProductUrl(e.target.value)}
                />
              ) : (
                <Stack spacing={1.5}>
                  <Button variant="outlined" component="label" sx={{ py: 1.5, alignSelf: "flex-start" }}>
                    {lang?.chooseImage || "Choose image"}
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileChange}
                    />
                  </Button>
                  {productFile ? (
                    <Typography variant="body2" color="text.secondary">
                      {productFile.name}
                    </Typography>
                  ) : null}
                  {productPreviewUrl ? (
                    <Box
                      component="img"
                      src={productPreviewUrl}
                      alt=""
                      sx={{
                        maxWidth: 280,
                        maxHeight: 200,
                        borderRadius: 2,
                        objectFit: "contain",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                  ) : null}
                </Stack>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={4}
              label={lang?.researchPromptLabel || "Research prompt"}
              placeholder={
                lang?.researchPromptPlaceholder ||
                "What should we learn? (audience, competitors, angles, objections…)"
              }
              value={researchPrompt}
              onChange={(e) => setResearchPrompt(e.target.value)}
            />
          </Stack>
        )}

        {/* Step 2 — Review research */}
        {activeStep === 2 && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ManageSearchOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {lang?.researchOutputLabel || "Research output"}
              </Typography>
            </Stack>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: 420,
                overflow: "auto",
                bgcolor: alpha(theme.palette.background.default, 0.6),
              }}
            >
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  fontSize: "0.875rem",
                  lineHeight: 1.65,
                }}
              >
                {researchResult || lang?.noResearchYet || "No research loaded."}
              </Typography>
            </Paper>
            <Tooltip title={lang?.copy || "Copy"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!researchResult}
                  onClick={() => copyText(researchResult, "Research")}
                >
                  <ContentCopyOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}

        {/* Step 3 — Script prompt */}
        {activeStep === 3 && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ArticleOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {lang?.ugcStep2Heading || "Step 4 — Generate 10 scripts"}
              </Typography>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={6}
              disabled={!workflowId || !researchResult}
              label={lang?.scriptPromptLabel || "Scriptwriting prompt"}
              placeholder={
                lang?.scriptPromptPlaceholder ||
                "Describe tone, length, platform (e.g. TikTok), must-include claims, CTA style…"
              }
              value={scriptPrompt}
              onChange={(e) => setScriptPrompt(e.target.value)}
            />
            {!researchResult ? (
              <Typography variant="caption" color="warning.main">
                {lang?.goBackForResearch || "Go back to complete research first."}
              </Typography>
            ) : null}
          </Stack>
        )}

        {/* Step 4 — Scripts + character + MakeUGC */}
        {activeStep === 4 && (
          <Stack spacing={3}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Face2 color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  {lang?.ugcScriptsCharacterHeading || "Character for this batch"}
                </Typography>
                {savingCharacter ? <CircularProgress size={20} /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {lang?.ugcScriptsCharacterHint ||
                  "Tap another character to switch who this workflow is for (saved automatically)."}
              </Typography>
              {activeCharacters.length > 0 ? (
                <Grid container spacing={1.5}>
                  {activeCharacters.map((m) => (
                    <Grid item xs={6} sm={4} md={3} key={m.id}>
                      <ModelCard
                        model={m}
                        isSelected={selectedModel?.id === m.id}
                        onSelect={() => saveWorkflowCharacter(m)}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : null}
            </Stack>

            <DividerMini theme={theme} />

            <Stack direction="row" alignItems="center" spacing={1}>
              <ArticleOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {lang?.yourScripts || "Your scripts"}
              </Typography>
            </Stack>

            {scripts?.length === 10 ? (
              <Stack spacing={1} sx={{ maxHeight: 480, overflow: "auto", pr: 0.5 }}>
                {scripts.map((s, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      position: "relative",
                      pr: 6,
                    }}
                  >
                    <Typography variant="caption" color="primary" fontWeight={700} display="block">
                      {lang?.scriptNumber || "Script"} {idx + 1}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
                      {s}
                    </Typography>
                    <Tooltip title={lang?.copy || "Copy"}>
                      <IconButton
                        size="small"
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => copyText(s, `Script ${idx + 1}`)}
                      >
                        <ContentCopyOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {lang?.scriptsPendingHint || "Generate scripts from the previous step to see them here."}
              </Typography>
            )}

            <DividerMini theme={theme} />

            <Stack direction="row" alignItems="center" spacing={1}>
              <SlowMotionVideo color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                {lang?.ugcKlingAvatarHeading || "Talking-head video (Kling AI Avatar)"}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {lang?.ugcKlingAvatarBody ||
                "Uses kie.ai createTask (Kling AI Avatar): your character photo plus speech from the script you pick. Requires KIE_AI_API_KEY (or KIE_API_KEY). If OPENAI_API_KEY is set, OpenAI TTS is used; otherwise Kie Market ElevenLabs TTS uses the same Kie key. Tune models and voice in Admin → AI Providers → UGC Kling avatar settings."}
            </Typography>
            {avatarErrorMessage && !avatarJobId ? (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                {avatarErrorMessage}
              </Alert>
            ) : null}
            {avatarJobId ? (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                {lang?.ugcAvatarGenerating || "Video is generating… this page refreshes status every few seconds."}
              </Alert>
            ) : null}
            {scripts?.length === 10 ? (
              <Stack spacing={2.5}>
                <TextField
                  select
                  size="small"
                  label={lang?.ugcAvatarScriptLabel || "Script for video"}
                  value={avatarScriptPick}
                  onChange={(e) => setAvatarScriptPick(Number(e.target.value))}
                  sx={{ minWidth: 220, maxWidth: 360 }}
                  disabled={Boolean(avatarJobId) || loadingAvatar}
                >
                  {scripts.map((_, i) => (
                    <MenuItem key={i} value={i}>
                      {(lang?.scriptNumber || "Script") + ` ${i + 1}`}
                    </MenuItem>
                  ))}
                </TextField>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {lang?.ugcAvatarVoiceHeading || "Voice template & preview"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {lang?.ugcAvatarVoiceHint ||
                      "Filter by gender and region, pick a template, then preview before generating the talking-head."}
                  </Typography>

                  {avatarTtsMode === "kie" ? (
                    <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={avatarGenderFilter}
                        onChange={(_e, v) => v != null && setAvatarGenderFilter(v)}
                        disabled={Boolean(avatarJobId) || loadingAvatar}
                      >
                        <ToggleButton value="all">{lang?.ugcVoiceGenderAll || "All"}</ToggleButton>
                        <ToggleButton value="female">{lang?.ugcVoiceGenderFemale || "Female"}</ToggleButton>
                        <ToggleButton value="male">{lang?.ugcVoiceGenderMale || "Male"}</ToggleButton>
                      </ToggleButtonGroup>
                      <TextField
                        select
                        size="small"
                        label={lang?.ugcVoiceRegionLabel || "Region / market"}
                        value={avatarRegionFilter}
                        onChange={(e) => setAvatarRegionFilter(e.target.value)}
                        sx={{ minWidth: 220, maxWidth: 360 }}
                        disabled={Boolean(avatarJobId) || loadingAvatar}
                      >
                        {AVATAR_REGION_FILTERS.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {lang?.ugcAvatarOpenAiVoiceHint || "OpenAI speech voices (set OPENAI_API_KEY on the server)."}
                    </Typography>
                  )}

                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={lang?.ugcVoiceTemplateLabel || "Voice template"}
                    value={avatarVoiceId}
                    onChange={(e) => setAvatarVoiceId(e.target.value)}
                    disabled={
                      Boolean(avatarJobId) ||
                      loadingAvatar ||
                      !(avatarTtsMode === "kie" ? filteredKieVoices : avatarOpenaiVoices).length
                    }
                    sx={{ maxWidth: 480 }}
                  >
                    {(avatarTtsMode === "kie" ? filteredKieVoices : avatarOpenaiVoices).map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {avatarTtsMode === "kie" && !filteredKieVoices.length ? (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                      {lang?.ugcVoiceNoMatch ||
                        "No voices match these filters — set region to “All regions” or pick another gender."}
                    </Typography>
                  ) : null}

                  <TextField
                    size="small"
                    fullWidth
                    label={lang?.ugcVoicePreviewCustomLabel || "Custom line for API preview (optional)"}
                    placeholder={lang?.ugcVoicePreviewCustomPh || "e.g. Hey everyone, wait until you hear this…"}
                    value={avatarPreviewText}
                    onChange={(e) => setAvatarPreviewText(e.target.value)}
                    sx={{ mt: 1.5 }}
                    disabled={Boolean(avatarJobId)}
                    inputProps={{ maxLength: 400 }}
                  />

                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }} alignItems="center">
                    {avatarTtsMode === "kie" ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VolumeUpOutlined />}
                        onClick={playAvatarStyleSample}
                        disabled={Boolean(avatarJobId) || !avatarVoiceId || !filteredKieVoices.length}
                      >
                        {lang?.ugcVoicePlayStyleSample || "Play style sample"}
                      </Button>
                    ) : null}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => requestAvatarPreviewTts("custom")}
                      disabled={
                        avatarPreviewLoading || Boolean(avatarJobId) || !avatarVoiceId || !avatarPreviewText.trim()
                      }
                    >
                      {lang?.ugcVoicePreviewCustomBtn || "Preview custom line"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => requestAvatarPreviewTts("script")}
                      disabled={avatarPreviewLoading || Boolean(avatarJobId) || !avatarVoiceId}
                    >
                      {lang?.ugcVoicePreviewScriptBtn || "Preview first lines of script"}
                    </Button>
                    {avatarPreviewLoading ? <CircularProgress size={22} /> : null}
                  </Stack>

                  {avatarPreviewUrl ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {lang?.ugcVoiceTtsPreviewLabel || "TTS preview"}
                      </Typography>
                      <audio
                        key={avatarPreviewUrl}
                        src={avatarPreviewUrl}
                        controls
                        style={{ width: "100%", maxWidth: 420 }}
                      />
                    </Box>
                  ) : null}
                </Paper>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={Boolean(avatarJobId) || loadingAvatar || !avatarVoiceId}
                    onClick={startAvatarVideo}
                    startIcon={loadingAvatar ? <CircularProgress size={18} color="inherit" /> : <SlowMotionVideo />}
                  >
                    {loadingAvatar
                      ? lang?.ugcAvatarStarting || "Starting…"
                      : lang?.ugcAvatarGenerate || "Generate talking-head"}
                  </Button>
                </Stack>
              </Stack>
            ) : null}
            {avatarVideo ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {lang?.ugcAvatarPreviewLabel || "Latest avatar video"}
                </Typography>
                <Box
                  component="video"
                  src={mediaSrc(avatarVideo)}
                  controls
                  playsInline
                  sx={{
                    width: "100%",
                    maxWidth: 420,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
              </Box>
            ) : null}

            <DividerMini theme={theme} />

            <Stack direction="row" alignItems="center" spacing={1}>
              <MovieCreationOutlined color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                {lang?.ugcStep3Title || "Create videos in MakeUGC"}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {lang?.ugcStep3Body ||
                "Bring scripts into MakeUGC (or any avatar tool). Pick character, format, paste script, generate."}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Button variant="outlined" size="small" startIcon={<Face2 />} onClick={() => goToUserPage("influencers")}>
                {lang?.manageCharacters || "Characters"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SlowMotionVideo />}
                onClick={() => goToUserPage("content")}
              >
                {lang?.openContent || "Content"}
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Step 5 — Scale + app CTA */}
        {activeStep === 5 && (
          <Stack spacing={3}>
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              {lang?.ugcVideoWhereInfo ||
                "You can generate a talking-head clip from the Scripts step (Kling AI Avatar). For cinematic product shots (kie Veo), open Script to Video when you are happy with your scripts. Product Showcase uses a different API — see docs/ugc-ads-cursor-prompt.md."}
            </Alert>
            <Stack direction="row" alignItems="center" spacing={1}>
              <RocketLaunchOutlined color="primary" />
              <Typography variant="h6" fontWeight={700}>
                {lang?.ugcStep4Title || "Scale"}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              {lang?.ugcStep4Body ||
                "Run all ten scripts through the same avatar and format for a batch of finished ads."}
            </Typography>

            {selectedModel?.name ? (
              <Chip icon={<Face2 sx={{ "&&": { fontSize: 18 } }} />} label={selectedModel.name} sx={{ alignSelf: "flex-start" }} />
            ) : null}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {lang?.ugcClosingTitle || "Product videos in this app"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {lang?.ugcClosingBody ||
                  "Use Script to Video for cinematic product shots with your characters when you are ready."}
              </Typography>
              <Button variant="contained" endIcon={<ArrowForwardOutlined />} onClick={() => goToUserPage("script-to-video")}>
                {lang?.ugcClosingCta || "Open Script to Video"}
              </Button>
            </Paper>
          </Stack>
        )}

        {/* Nav */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <Button startIcon={<ArrowBackOutlined />} disabled={activeStep === 0} onClick={handleBack}>
            {lang?.back || "Back"}
          </Button>

          {showNext ? (
            <Button
              variant="contained"
              endIcon={
                activeStep === 1 && loadingResearch ? (
                  <CircularProgress size={18} color="inherit" />
                ) : activeStep === 3 && loadingScripts ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <ArrowForwardOutlined />
                )
              }
              disabled={nextDisabled}
              onClick={handleNext}
            >
              {activeStep === 1 && loadingResearch
                ? lang?.runningResearch || "Working…"
                : activeStep === 3 && loadingScripts
                  ? lang?.generatingScripts || "Generating…"
                  : nextLabel}
            </Button>
          ) : (
            <Chip label={lang?.wizardDone || "Done"} color="success" variant="outlined" />
          )}
        </Stack>
      </Paper>

      <Accordion defaultExpanded={false} sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RefreshOutlined fontSize="small" />
            <Typography fontWeight={600}>{lang?.workflowHistory || "Workflow history"}</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {history.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {lang?.noWorkflowsYet || "No saved workflows yet."}
              </Typography>
            ) : (
              history.map((row) => (
                <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        #{row.id}{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {row.status}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => loadWorkflow(row)}>
                        {lang?.load || "Load"}
                      </Button>
                      <IconButton size="small" color="error" onClick={() => deleteWorkflow(row.id)}>
                        <DeleteOutline />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

function DividerMini({ theme }) {
  return <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, my: 1 }} />;
}

export default UgcAdsWorkflow;
